/**
 * DuplicateIncidentBanner — client-side dedup heads-up shown on the intake
 * form when the user's draft looks similar to an open incident from the last
 * 24 hours. Two people can hit the report form at the same time for the
 * same event (e.g. a building fire); without this they each get their own
 * incident record and the room ends up with two SitReps to merge later.
 *
 * Layer 1 only: a non-blocking banner with a "View existing" link. It does
 * NOT prevent submission — the operator can confirm "yes this is genuinely
 * a separate incident" by simply continuing. The merge endpoint (commit 6)
 * picks up the rest if a true dup makes it through.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { listIncidents } from '../api/incidents';
import { Incident } from '../types/incident';
import { scoreSimilarity } from '../utils/incidentSimilarity';

interface Props {
  // Optional — incident type is no longer collected on the report form (the
  // agent classifies it later), so the similarity check falls back to title/
  // location/description alone when `type` is missing.
  type?:       string;
  title:       string;
  location:    string;
  description: string;
  /** Called with the incident the user wants to navigate to. */
  onView?: (incident: Incident) => void;
}

const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;
const SCORE_THRESHOLD  = 0.45;

export function DuplicateIncidentBanner({ type = '', title, location, description, onView }: Props) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    listIncidents().then(setIncidents).catch(() => setIncidents([]));
  }, []);

  const match = useMemo(() => {
    if (!title.trim() || title.trim().length < 4) return null;
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    const candidates = incidents.filter((i) => {
      if (dismissed.has(i.id)) return false;
      if (i.status === 'CLOSED' || i.status === 'RESOLVED') return false;
      const occurred = new Date(i.occurredAt).getTime();
      return Number.isFinite(occurred) && occurred >= cutoff;
    });
    let best: { incident: Incident; score: number } | null = null;
    for (const c of candidates) {
      const score = scoreSimilarity({ type, title, location, description }, c);
      if (score >= SCORE_THRESHOLD && (!best || score > best.score)) {
        best = { incident: c, score };
      }
    }
    return best;
  }, [incidents, type, title, location, description, dismissed]);

  if (!match) return null;
  const { incident, score } = match;

  return (
    <div style={{
      background: 'rgba(245,158,11,0.12)',
      border: '1px solid rgba(245,158,11,0.45)',
      borderRadius: 10,
      padding: '12px 16px',
      marginBottom: 18,
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{ fontSize: 20, lineHeight: 1, marginTop: 2 }}>⚠️</div>
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.55, color: 'var(--fg)' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          This looks similar to an open incident — are you reporting the same event?
        </div>
        <div style={{ color: 'var(--muted)', marginBottom: 8 }}>
          <strong style={{ color: 'var(--fg)' }}>{incident.id}</strong>
          {' · '}{incident.title}
          {' · '}{incident.location}
          {' · opened '}{new Date(incident.occurredAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', dateStyle: 'short', timeStyle: 'short' })}
          {' · '}<span style={{ color: 'var(--subtle)' }}>match {Math.round(score * 100)}%</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {onView && (
            <button
              type="button"
              onClick={() => onView(incident)}
              style={{
                background: 'rgba(245,158,11,0.20)',
                border: '1px solid rgba(245,158,11,0.50)',
                color: '#92400e',
                borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Open {incident.id}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed((d) => new Set(d).add(incident.id))}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-md)',
              color: 'var(--muted)',
              borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Not the same incident — continue
          </button>
        </div>
      </div>
    </div>
  );
}
